import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useUpdateEffect } from 'react-use';
import { Key } from 'w3c-keys';
import { RD_COMPONENTS, useKeyPressNavigation, } from '../../_utils';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { Colors, Typography } from '../../typography';
import { createBaseSelect, defaultSelectedValue, resetSelectedOptionId, } from '../base-select';
import styles from '../select.module.scss';
import { useSelect } from '../select-util';
export const Dropdown = ({ optionsGroups, onChange, selectedOptions, disabled, isCompactField, hasGrayBackground, placeholder, classList, multiSelect, onMenuToggle, onSearchInput, variant = 'default', headerFontStyle = 'default', shouldShowSelectedImage = false, hasError = false, hasWarning = false, helperText, dropdownWidth = 'defaultWidth', resetSelectedOption, labelTruncationLimit, showLabelTooltip, preserveWhitespace, canSearchSubText, renderBadge, customDropdownContent, customCurrentSelectionDisplay, isMenuVisible = false, enableScroll = true, shouldAutoFocusSearch = false, searchPlaceholder, ...restProps }) => {
    const getDefaultLabel = useCallback(() => optionsGroups
        .map((optionsGroup) => optionsGroup.options.filter(({ id: OptionId }) => selectedOptions &&
        OptionId === selectedOptions[0])[0])
        .filter((option) => option)[0] || {}, [optionsGroups, selectedOptions]);
    const { id: defaultId = '', label: defaultLabel = '', imagePath: defaultImagePath = '', subText: defaultSubText = '', } = getDefaultLabel();
    const initSelectedValues = !multiSelect
        ? {
            id: defaultId,
            label: defaultLabel,
            imagePath: defaultImagePath,
            subText: defaultSubText,
        }
        : defaultSelectedValue;
    if (resetSelectedOption === null || resetSelectedOption === void 0 ? void 0 : resetSelectedOption.showResetSelectedOption) {
        // As we are appending the noInput option to the start of optionsGroups
        // Adding the check to ensure that we do not add multiple of these
        if (optionsGroups.length > 0 &&
            optionsGroups[0].options.length > 0 &&
            optionsGroups[0].options[0].id !== resetSelectedOptionId) {
            const noInput = {
                options: [
                    {
                        id: resetSelectedOptionId,
                        label: resetSelectedOption.resetSelectedOptionLabel || '',
                        data: {
                            resetDataInfo: resetSelectedOption.resetDataInfo,
                        },
                    },
                ],
            };
            optionsGroups.unshift(noInput);
        }
    }
    const { visible, setVisible, selectedValue, setSelectedValue, searchTerm, setSearchTerm, optionsResults, } = useSelect(isMenuVisible, optionsGroups, onSearchInput, initSelectedValues, canSearchSubText);
    useEffect(() => {
        if (!multiSelect) {
            const { id = '', label = '', imagePath = '', subText = '', } = getDefaultLabel();
            setSelectedValue({
                id,
                label,
                imagePath,
                subText,
            });
        }
    }, [getDefaultLabel, multiSelect, selectedOptions, setSelectedValue]);
    const [currentSelectedOptions, setSelectedOptions] = useState(selectedOptions || []);
    const flattenedOptions = optionsResults.reduce((acc, currentvalue) => [
        ...acc,
        ...currentvalue.options,
    ], []);
    const nonMulitSelectCB = (item) => {
        const { id, label, data, imagePath = '', subText } = item;
        if (id === resetSelectedOptionId) {
            setSelectedValue(defaultSelectedValue);
            if (onChange)
                onChange({
                    id: '',
                    label: '',
                    data,
                    imagePath: '',
                });
        }
        else {
            setSelectedValue({ id, label, imagePath, subText });
            if (onChange)
                onChange({ id, label, data, imagePath, subText });
        }
        setVisible(!visible);
    };
    /** To get return the selected options on change if
     * multiSelect option is true
     */
    const getSelectedOptionsonChange = (filteredOptions) => {
        const optionsKeyValue = new Map();
        flattenedOptions.forEach((option) => optionsKeyValue.set(option.id, option));
        const resultSelectedOptions = [];
        filteredOptions.forEach((key) => resultSelectedOptions.push(optionsKeyValue.get(key)));
        return resultSelectedOptions;
    };
    const onDropdownSelect = (item) => {
        if (customDropdownContent) {
            setVisible(false);
            return;
        }
        if (item && visible) {
            const { id } = item;
            let filteredOptions;
            if (currentSelectedOptions.indexOf(id) >= 0) {
                filteredOptions = currentSelectedOptions.filter((option) => option !== id);
                setSelectedOptions([...filteredOptions]);
            }
            else {
                filteredOptions = [...currentSelectedOptions, id];
                setSelectedOptions(filteredOptions);
            }
            if (multiSelect && onChange) {
                onChange(getSelectedOptionsonChange(filteredOptions));
            }
            if (!multiSelect) {
                nonMulitSelectCB(item);
            }
            if (!onSearchInput) {
                setSearchTerm('');
            }
        }
    };
    const menuRef = useRef(null);
    const dropDownHeaderRef = useRef(null);
    // Search Input Ref
    const inputRef = useRef(null);
    // Footer Ref
    const footerRef = useRef(null);
    const { cursor, setCursor } = useKeyPressNavigation(document, flattenedOptions, onDropdownSelect, visible);
    const handleDocumentEvent = useCallback((event) => {
        if ((menuRef.current && menuRef.current.contains(event.target)) ||
            (dropDownHeaderRef.current &&
                dropDownHeaderRef.current.contains(event.target))) {
            // click handler inside select menu options
            return;
        }
        setVisible(false);
    }, [setVisible]);
    const closeDropdown = () => {
        setVisible(false);
        const elem = dropDownHeaderRef === null || dropDownHeaderRef === void 0 ? void 0 : dropDownHeaderRef.current;
        elem === null || elem === void 0 ? void 0 : elem.focus();
    };
    const stickyFooterClickCallback = () => {
        if (restProps.stickyFooter) {
            setSelectedValue(defaultSelectedValue);
            if (onChange)
                onChange({ id: '', label: '', data: '', imagePath: '' });
            closeDropdown();
        }
    };
    const checkCurrentFocused = (element) => {
        return document.activeElement === element;
    };
    // this is to handle visiblity of menu from within menu item upon pressing escape or tab and to focus dropdown header after closing
    const onKeyPressCB = (event) => {
        const inputElement = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
        const footerElement = footerRef === null || footerRef === void 0 ? void 0 : footerRef.current;
        switch (true) {
            case event.shiftKey && event.key === Key.Tab:
                // this is to handle event propagation in case of navigating inside the footer
                if (!checkCurrentFocused(inputElement) &&
                    !checkCurrentFocused(footerElement) &&
                    cursor === -1)
                    break;
                event.preventDefault();
                event.stopPropagation();
                // Check if the inputElement is already focused or if it is present
                if (!inputElement || document.activeElement === inputElement) {
                    closeDropdown();
                }
                else {
                    setCursor(-1);
                    inputElement === null || inputElement === void 0 ? void 0 : inputElement.focus();
                }
                break;
            case event.key === Key.Tab:
                event.stopPropagation();
                if (!footerElement) {
                    closeDropdown();
                }
                else if (document.activeElement === inputElement ||
                    cursor !== -1) {
                    event.preventDefault();
                    setCursor(-1);
                    footerElement === null || footerElement === void 0 ? void 0 : footerElement.focus();
                }
                break;
            case event.key === Key.Escape:
                event.stopPropagation();
                closeDropdown();
                break;
            default:
                break;
        }
    };
    useEffect(() => {
        var _a;
        if (cursor >= 0 && !!flattenedOptions[cursor]) {
            // this way all menu items will be accessed for screenreader even for those having id as number
            const ele = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[id="${flattenedOptions[cursor].id}"]`);
            ele === null || ele === void 0 ? void 0 : ele.focus();
        }
    }, [cursor, menuRef, flattenedOptions]);
    useEffect(() => {
        document.addEventListener('mousedown', handleDocumentEvent, {
            capture: false,
        });
        return () => {
            document.removeEventListener('mousedown', handleDocumentEvent, {
                capture: false,
            });
        };
    }, [handleDocumentEvent, menuRef]);
    useEffect(() => {
        setVisible(isMenuVisible);
    }, [isMenuVisible, setVisible]);
    useUpdateEffect(() => {
        if (onMenuToggle) {
            onMenuToggle(visible);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);
    const renderIcon = (iconName) => (React.createElement(Icon, { color: IconColor.GRAY, size: IconSize.MEDIUM, id: iconName }));
    const getHelpTextMarkup = () => {
        if (helperText) {
            const helperTextClassName = cx(styles.helperText, {
                [styles.hasError]: hasError,
                [styles.hasWarning]: hasWarning,
                [styles.disabled]: disabled,
            });
            return (React.createElement("span", { className: helperTextClassName },
                hasError && (React.createElement("span", { className: styles.iconStyle },
                    React.createElement(Icon, { color: IconColor.TEXT_COLOR, size: IconSize.XSMALL, id: IconID.EXCLAMATION_POINT_CIRCLE }))),
                helperText));
        }
        return null;
    };
    const renderHeader = () => {
        const onHeaderClick = (event) => {
            setVisible(!visible);
            event.stopPropagation();
        };
        const onKeyDown = (event) => {
            if (optionsGroups && event.key === 'Enter') {
                onDropdownSelect(flattenedOptions[cursor]);
                setVisible(!visible);
                event.stopPropagation();
            }
            if (optionsGroups &&
                (event.key === Key.Escape || event.key === Key.Tab)) {
                setVisible(false);
                event.stopPropagation();
            }
        };
        const isRich = variant === 'richSubtext';
        /** Header Label Logic to show based on the multiselect options
         */
        const getHeaderLabel = () => {
            var _a;
            if (!multiSelect) {
                return selectedValue.label;
            }
            /** To show the placeholder if there is no options selected */
            const optionsLen = currentSelectedOptions.length;
            if (optionsLen === 0)
                return placeholder;
            const firstOptionLabel = ((_a = flattenedOptions.find((item) => item.id === currentSelectedOptions[0])) === null || _a === void 0 ? void 0 : _a.label) || '';
            return optionsLen === 1
                ? firstOptionLabel
                : `${firstOptionLabel} +${optionsLen - 1}`;
        };
        const getCurrentSelectedOption = () => {
            if (!_.isNil(customCurrentSelectionDisplay))
                return customCurrentSelectionDisplay;
            let selectedLabel = placeholder;
            let selectedImagePath = '';
            if (selectedOptions) {
                const selectedOption = flattenedOptions.filter((item) => item.id === selectedOptions[0]);
                // if there is an invalid item return placeholder text is returned
                if (selectedOption.length) {
                    if (shouldShowSelectedImage &&
                        !multiSelect &&
                        selectedValue.imagePath) {
                        selectedImagePath = selectedValue.imagePath;
                    }
                    else {
                        selectedLabel = getHeaderLabel();
                    }
                }
            }
            if (!multiSelect && selectedValue) {
                if (shouldShowSelectedImage && selectedValue.imagePath) {
                    selectedImagePath = selectedValue.imagePath;
                }
                else {
                    selectedLabel = selectedValue.label
                        ? selectedValue.label
                        : placeholder;
                }
            }
            const selectValueClass = cx(classList === null || classList === void 0 ? void 0 : classList.selectValue, styles.selectedValue, {
                [styles.placeholder]: selectedLabel === placeholder,
                [styles.badge]: renderBadge,
            });
            const renderSelectedLabel = () => {
                const text = selectedLabel || '';
                return headerFontStyle === 'bold' ? React.createElement("b", null, text) : text;
            };
            if (selectedImagePath) {
                return (React.createElement("img", { src: selectedImagePath, className: selectValueClass, alt: selectedLabel }));
            }
            const labelElement = (React.createElement(Typography, { variant: "body-normal", className: selectValueClass, ...(_.isUndefined(preserveWhitespace)
                    ? {}
                    : { preserveWhitespace }), ellipsis: { rows: 1 }, tooltipConfig: {
                    dataTooltipContent: selectedLabel || '',
                } },
                renderSelectedLabel(),
                renderBadge ? (React.createElement("span", { className: styles.selectBadge })) : null));
            return isRich ? (React.createElement("div", { className: styles.selectValueContainer },
                labelElement,
                !multiSelect && selectedValue.subText && (React.createElement(Typography, { variant: "footnote", color: Colors.info, className: styles.selectSubtext }, selectedValue.subText)))) : (labelElement);
        };
        const isRounded = variant === 'roundedHead';
        const selectCaretClass = cx(classList === null || classList === void 0 ? void 0 : classList.selectCaret, styles.selectCaret, { [styles.selectOpen]: visible });
        const selectHeaderClass = cx(classList === null || classList === void 0 ? void 0 : classList.selectHeadClass, styles.selectHead, {
            [styles.selectOpen]: visible,
            [styles.disabled]: disabled,
            [styles.isCompactField]: isCompactField,
            [styles.hasGrayBackground]: hasGrayBackground,
            [styles.roundedHead]: isRounded,
            [styles.hasError]: hasError,
            [styles.hasWarning]: hasWarning,
            [styles.withSubtext]: isRich && !multiSelect && !!selectedValue.subText,
        });
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { ref: dropDownHeaderRef, className: selectHeaderClass, onClick: onHeaderClick, role: "button", "aria-haspopup": "true", onKeyDown: onKeyDown, 
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex: disabled ? -1 : 0, "data-testid": "select-dropdown-header", "aria-expanded": !!visible },
                getCurrentSelectedOption(),
                React.createElement("div", { className: selectCaretClass }, visible
                    ? renderIcon(IconID.CHEVRON_UP)
                    : renderIcon(IconID.CHEVRON_DOWN))),
            getHelpTextMarkup()));
    };
    const dropDownHeader = renderHeader();
    const updatedOptions = useMemo(() => optionsResults.map((optionGroup) => ({
        ...optionGroup,
        options: optionGroup.options.map((option) => ({
            ...option,
            active: selectedValue.id === option.id,
        })),
    })), [optionsResults, selectedValue.id]);
    const dropDownProps = {
        ...restProps,
        classList: {
            selectBodyClass: (classList === null || classList === void 0 ? void 0 : classList.selectBodyClass)
                ? classList === null || classList === void 0 ? void 0 : classList.selectBodyClass
                : '',
        },
        isMenuVisible: visible,
        selectedOptions: currentSelectedOptions,
        multiSelect,
        optionsGroups: updatedOptions,
        dropdownWidth,
        showLabelTooltip: showLabelTooltip !== null && showLabelTooltip !== void 0 ? showLabelTooltip : true,
        labelTruncationLimit,
        canSearchSubText,
        onKeyPressCB,
        shouldAutoFocusSearch,
        inputRef,
        footerRef,
        searchPlaceholder,
        variant,
    };
    if (variant === 'roundedHead')
        dropDownProps.bottomOffset = 4;
    return createBaseSelect(dropDownProps, {
        searchTerm,
        setSearchTerm,
        dropDownHeader,
        optionHighlighted: flattenedOptions[cursor],
        onSelectCallback: onDropdownSelect,
        selectMenuRef: menuRef,
        dropDownHeaderRef,
        customDropdownContent,
        enableScroll,
        stickyFooterClickCallback,
    });
};
Dropdown.RD_COMPONENT = RD_COMPONENTS.DROPDOWN;
//# sourceMappingURL=select-dropdown.js.map